<template>
	<div class="bg-primary-25 w-full p-6 print:py-2">
		<section>
			<h2
				class="form-title pb-6 text-2xl font-bold print:mb-3 print:border-b-2 print:border-slate-900 print:pt-2 print:pb-3 print:text-sm/0"
			>
				{{ t('plan.sectionTitle.occupationAndDemographics') }}
			</h2>
			<div
				class="section-grid-row grid grid-cols-3 *:w-full *:max-w-none *:px-1 *:pb-1"
			>
				<FormKit
					type="textarea"
					auto-height
					name="occupation"
					:label="t('plan.occupation')"
				/>

				<FormKit
					type="number"
					name="occupationYears"
					:label="t('plan.occupationYears')"
					:value="String(0)"
					step="1"
					validation="between:0,125"
					validation-visibility="live"
				/>

				<FormKit
					type="textarea"
					auto-height
					name="businessType"
					:label="t('plan.businessType')"
				/>
			</div>

			<div
				class="section-grid-row grid grid-cols-4 *:w-full *:max-w-none *:px-1 *:pb-1"
			>
				<FormKit
					type="dropdown"
					:label="t('plan.educationLevel')"
					name="educationLevel"
					:options="educationLevelOptions"
					:placeholder="
						t('plan.placeholders.select', {
							placeholderLabel: t('plan.educationLevel'),
						})
					"
				/>

				<FormKit
					type="dropdown"
					:label="t('plan.race')"
					name="race"
					:options="raceOptions"
					:placeholder="
						t('plan.placeholders.select', {
							placeholderLabel: t('plan.race'),
						})
					"
				/>

				<FormKit
					type="dropdown"
					:label="t('plan.gender')"
					name="gender"
					:options="genderOptions"
					:placeholder="
						t('plan.placeholders.select', {
							placeholderLabel: t('plan.gender'),
						})
					"
				/>

				<FormKit
					type="dropdown"
					:label="t('plan.maritalStatus')"
					name="maritalStatus"
					:options="maritalStatusOptions"
					:placeholder="
						t('plan.placeholders.select', {
							placeholderLabel: t('plan.maritalStatus'),
						})
					"
				/>
			</div>

			<div
				class="section-grid-row grid grid-cols-1 *:w-full *:max-w-none *:px-1"
				:class="{ 'print:hidden': !plan?.vitalInfoNotes?.trim() }"
			>
				<FormKit
					type="textarea"
					auto-height
					name="vitalInfoNotes"
					:label="t('plan.notes')"
					:outer-class="{ 'mb-0': true }"
					:placeholder="t('plan.placeholders.vitalInfoNotes')"
					validation="length:0,10000"
					validation-visibility="live"
				/>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import { EducationLevelOptions } from '@/enums/EducationLevel'
import { GenderOptions } from '@/enums/Gender'
import { MaritalStatus } from '@/enums/MaritalStatus'
import { RaceOptions } from '@/enums/Race'
import { usePresentationLocale } from '@/i18n'
import { FormKit } from '@formkit/vue'

const { t } = usePresentationLocale()

defineProps<{
	plan: Plan
}>()

/**
 * The options for race.
 */
const raceOptions = [
	{
		label: t('plan.raceOptions.americanIndianOrAlaskaNative'),
		value: RaceOptions.AmericanIndianOrAlaskaNative,
	},
	{
		label: t('plan.raceOptions.asian'),
		value: RaceOptions.Asian,
	},
	{
		label: t('plan.raceOptions.blackOrAfricanAmerican'),
		value: RaceOptions.BlackOrAfricanAmerican,
	},
	{
		label: t('plan.raceOptions.hispanicOrLatin'),
		value: RaceOptions.HispanicOrLatin,
	},
	{
		label: t('plan.raceOptions.nativeHawaiianOrOtherPacificIslander'),
		value: RaceOptions.NativeHawaiianOrOtherPacificIslander,
	},
	{
		label: t('plan.raceOptions.white'),
		value: RaceOptions.White,
	},
	{
		label: t('plan.raceOptions.other'),
		value: RaceOptions.Other,
	},
]

/**
 * The options for education level.
 */
const educationLevelOptions = [
	{
		label: t('plan.educationLevelOptions.someHighSchool'),
		value: EducationLevelOptions.SomeHighSchool,
	},
	{
		label: t('plan.educationLevelOptions.highSchoolDiplomaGED'),
		value: EducationLevelOptions.HighSchoolDiplomaGED,
	},
	{
		label: t('plan.educationLevelOptions.someCollege'),
		value: EducationLevelOptions.SomeCollege,
	},
	{
		label: t('plan.educationLevelOptions.associateDegree'),
		value: EducationLevelOptions.AssociateDegree,
	},
	{
		label: t('plan.educationLevelOptions.bachelorDegree'),
		value: EducationLevelOptions.BachelorDegree,
	},
	{
		label: t('plan.educationLevelOptions.masterDegree'),
		value: EducationLevelOptions.MasterDegree,
	},
	{
		label: t('plan.educationLevelOptions.doctorate'),
		value: EducationLevelOptions.Doctorate,
	},
	{
		label: t('plan.educationLevelOptions.professionalDegree'),
		value: EducationLevelOptions.ProfessionalDegree,
	},
	{
		label: t('plan.educationLevelOptions.other'),
		value: EducationLevelOptions.Other,
	},
]

/**
 * The options for gender.
 */
const genderOptions = [
	{
		label: t('plan.genderOptions.male'),
		value: GenderOptions.Male,
	},
	{
		label: t('plan.genderOptions.female'),
		value: GenderOptions.Female,
	},
	{
		label: t('plan.genderOptions.nonBinary'),
		value: GenderOptions.NonBinary,
	},
	{
		label: t('plan.genderOptions.other'),
		value: GenderOptions.Other,
	},
]

const maritalStatusOptions = [
	{
		label: t('plan.maritalStatusOptions.single'),
		value: MaritalStatus.Single,
	},
	{
		label: t('plan.maritalStatusOptions.married'),
		value: MaritalStatus.Married,
	},
	{
		label: t('plan.maritalStatusOptions.separated'),
		value: MaritalStatus.Separated,
	},
	{
		label: t('plan.maritalStatusOptions.divorced'),
		value: MaritalStatus.Divorced,
	},
	{
		label: t('plan.maritalStatusOptions.widowed'),
		value: MaritalStatus.Widowed,
	},
	{
		label: t('plan.maritalStatusOptions.domesticPartnership'),
		value: MaritalStatus.DomesticPartnership,
	},
	{
		label: t('plan.maritalStatusOptions.inRelationship'),
		value: MaritalStatus.InRelationship,
	},
	{
		label: t('plan.maritalStatusOptions.preferNotToSay'),
		value: MaritalStatus.PreferNotToSay,
	},
]
</script>
